import React, { useState } from 'react';
import styled from 'styled-components';
import { FaPaperPlane, FaCheckCircle } from 'react-icons/fa';
import contactService from '../../services/contactService';

const NewsletterSection = styled.section`
  background-color: ${props => props.theme.colors.primaryColor};
  padding: 80px 0;
  text-align: center;
  color: white;
`;

const NewsletterContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const NewsletterTitle = styled.h2`
  color: white;
  font-size: ${props => props.theme.fontSizes.xxxlarge};
  margin-bottom: 15px;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.xxlarge};
  }
`;

const NewsletterDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.medium};
  margin-bottom: 30px;
  color: rgba(255, 255, 255, 0.8);
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.regular};
  }
`;

const FormContainer = styled.form`
  display: flex;
  max-width: 600px;
  margin: 0 auto;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 15px;
  border: none;
  border-radius: ${props => props.theme.borders.radius.small} 0 0 ${props => props.theme.borders.radius.small};
  font-family: ${props => props.theme.fonts.primary};
  font-size: ${props => props.theme.fontSizes.regular};
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    border-radius: ${props => props.theme.borders.radius.small} ${props => props.theme.borders.radius.small} 0 0;
    margin-bottom: 10px;
  }
`;

const SubmitButton = styled.button`
  background-color: ${props => props.theme.colors.secondaryColor};
  color: white;
  border: none;
  padding: 0 25px;
  border-radius: 0 ${props => props.theme.borders.radius.small} ${props => props.theme.borders.radius.small} 0;
  font-family: ${props => props.theme.fonts.primary};
  font-weight: ${props => props.theme.fontWeights.semiBold};
  font-size: ${props => props.theme.fontSizes.regular};
  cursor: pointer;
  transition: ${props => props.theme.transitions.main};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #1a252f;
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.grey[400]};
    cursor: not-allowed;
  }
  
  svg {
    margin-right: 8px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    border-radius: 0 0 ${props => props.theme.borders.radius.small} ${props => props.theme.borders.radius.small};
    padding: 12px 25px;
  }
`;

const ErrorMessage = styled.div`
  color: #fff;
  background-color: ${props => props.theme.colors.dangerColor};
  padding: 10px;
  margin-top: 15px;
  border-radius: ${props => props.theme.borders.radius.small};
  display: inline-block;
`;

const SuccessMessage = styled.div`
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 15px;
  
  svg {
    margin-right: 10px;
    font-size: 1.2rem;
  }
`;

const NewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic email validation
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      await contactService.subscribeToNewsletter({ email });
      setIsSubscribed(true);
      setEmail('');
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSubscribed(false);
      }, 5000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to subscribe. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <NewsletterSection>
      <div className="container">
        <NewsletterContent>
          <NewsletterTitle>Subscribe to Our Newsletter</NewsletterTitle>
          <NewsletterDescription>
            Stay updated with our latest courses, events, and success stories
          </NewsletterDescription>
          
          <FormContainer onSubmit={handleSubmit}>
            <Input
              type="email"
              placeholder="Your Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                'Subscribing...'
              ) : (
                <>
                  <FaPaperPlane />
                  Subscribe
                </>
              )}
            </SubmitButton>
          </FormContainer>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          {isSubscribed && (
            <SuccessMessage>
              <FaCheckCircle />
              Thank you for subscribing to our newsletter!
            </SuccessMessage>
          )}
        </NewsletterContent>
      </div>
    </NewsletterSection>
  );
};

export default NewsletterForm;
